<script setup lang="ts">
  import type { ISimpleSelectItem } from '@/components/ui/select/types'

  const modelValue = defineModel<ISimpleSelectItem>()

  const props = defineProps<{
    items: ISimpleSelectItem[]
    placeholder?: string
    triggerClass?: unknown[]
    ariaLabelledBy?: string
  }>()

  const { t } = useI18n()
  const placeholder = computed(() => props.placeholder ?? t('forms.select.placeholder.default'))

  const isSelectOpen = ref(false)
  const activeItem = ref<string>(modelValue.value?.label ?? '')

  watch(
    () => modelValue.value,
    () => {
      activeItem.value = modelValue.value?.label ?? ''
    },
    {
      immediate: true
    }
  )

  watch(
    () => activeItem.value,
    () => {
      const activeSelectItem = props.items.find((item) => item.label === activeItem.value)

      if (activeSelectItem?.id) {
        modelValue.value = activeSelectItem
      }
    }
  )
</script>
<template>
  <div class="flex flex-col">
    <SelectRoot
      v-model="activeItem"
      v-model:open="isSelectOpen"
    >
      <SelectTrigger
        class="flex items-center gap-2 outline-none"
        :class="triggerClass"
        :aria-labelledby="props.ariaLabelledBy"
        tabindex="0"
      >
        <SelectValue
          :placeholder
          as="span"
          class="mb-0 border-b border-inherit text-h5"
        />
        <Icon
          name="common/chevron-right"
          :class="isSelectOpen ? '-rotate-90' : 'rotate-90'"
        />
      </SelectTrigger>
      <SelectContent
        body-lock
        prioritize-position
        position="popper"
        :side-offset="8"
        class="z-10 max-h-64 overflow-hidden rounded-sm bg-white text-bt-blue-dark shadow-sm"
      >
        <ScrollAreaRoot>
          <SelectViewport
            as-child
            class="p-1"
          >
            <ScrollAreaViewport class="flex max-h-64 w-64 flex-col overflow-auto">
              <SelectItem
                v-for="item in items"
                :key="item.id"
                :value="item.label"
                class="mb-1 cursor-pointer rounded-xs px-2 py-4 last-of-type:mb-0 hover:bg-divider hover:outline-none data-[state=checked]:bg-divider data-[highlighted]:md:bg-divider"
              >
                <SelectItemText>
                  {{ item.label }}
                </SelectItemText>
              </SelectItem>
            </ScrollAreaViewport>
          </SelectViewport>
          <ScrollAreaScrollbar
            class="flex touch-none select-none p-0.5 transition-colors duration-[160ms] ease-out data-[orientation=vertical]:w-2.5"
            orientation="vertical"
          >
            <ScrollAreaThumb
              class="relative flex-1 rounded-[0.625rem] bg-border before:absolute before:left-1/2 before:top-1/2 before:h-full before:min-h-[2.75rem] before:w-full before:min-w-[2.75rem] before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']"
            />
          </ScrollAreaScrollbar>
        </ScrollAreaRoot>
      </SelectContent>
    </SelectRoot>
  </div>
</template>
